import React, { useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { ResponsiveContext } from 'grommet';
import { observer } from 'mobx-react-lite';
import {
  AddressAutocompleteInput,
  AddressAutocompletePlaceResult,
  Box,
  FormCardSection,
  Input,
  Select,
} from '/src/components';
import { useGlobalStore, useUserStore } from '/src/context';
import { ClientDataForm } from '/src/lib/types';
import { getIsMobile } from '/src/utils';

export const ClientCorporateInformation: React.FC<ClientCorporateInformationProps> = observer((props) => {
  /* Props */
  const { formValues, updateFormValue, formErrors, id, onSubmit } = props;

  /* Context */
  const userStore = useUserStore();
  const globalStore = useGlobalStore();
  const { t } = useTranslation();
  const screenSize = useContext(ResponsiveContext);
  const isMobile = getIsMobile(screenSize);

  const handlePlaceSelected = useCallback(
    (place: AddressAutocompletePlaceResult) => {
      if (place.line2) updateFormValue('hq_address_line2', place.line2);
      if (place.city) updateFormValue('hq_address_city', place.city);
      if (place.postCode) updateFormValue('hq_address_post_code', place.postCode);
      if (place.stateShortCode) {
        const region = globalStore.regions.find(
          (r) => r.short_code?.toLowerCase() === place.stateShortCode.toLowerCase()
        );
        if (region) updateFormValue('hq_address_region_id', region.id);
      }
    },
    [updateFormValue, globalStore.regions]
  );

  const isDisabled = !userStore.isFuseAdmin && (!userStore.isAdminUser || !userStore.isClientUser);

  return (
    <FormCardSection id={id} title={t('client:corporateAddress')}>
      <Box margin={{ bottom: '1rem' }} gap="1rem">
        <AddressAutocompleteInput
          name="hq_address_line1"
          label={t('common:address')}
          value={formValues.hq_address_line1}
          setValue={(value) => updateFormValue('hq_address_line1', value)}
          onPlaceSelected={handlePlaceSelected}
          error={formErrors['hq_address_line1']}
          onSubmit={onSubmit}
          required
          disabled={isDisabled}
        />
        <Input
          name="hq_address_line2"
          label={t('common:addressContinued')}
          value={formValues.hq_address_line2 ?? ''}
          setValue={(value) => updateFormValue('hq_address_line2', value)}
          error={formErrors['hq_address_line2']}
          onSubmit={onSubmit}
          disabled={isDisabled}
        />
        <Box direction={isMobile ? 'column' : 'row'} gap="1rem">
          <Box flex="grow">
            <Input
              name="hq_address_city"
              label={t('common:city')}
              value={formValues.hq_address_city}
              setValue={(value) => updateFormValue('hq_address_city', value)}
              error={formErrors['hq_address_city']}
              onSubmit={onSubmit}
              required
              disabled={isDisabled}
            />
          </Box>
          <Box flex="grow">
            <Select
              name="hq_address_region_id"
              label={t('common:stateProvince')}
              value={formValues.hq_address_region_id}
              setValue={(value) => updateFormValue('hq_address_region_id', value)}
              options={globalStore.regions.map((region) => ({
                label: `${region.name} (${region.short_code})`,
                value: region.id,
              }))}
              error={formErrors['hq_address_region_id']}
              required
              disabled={isDisabled}
            />
          </Box>
          <Box flex="grow">
            <Input
              name="hq_address_post_code"
              label={t('common:postalCode')}
              value={formValues.hq_address_post_code}
              setValue={(value) => updateFormValue('hq_address_post_code', value)}
              error={formErrors['hq_address_post_code']}
              onSubmit={onSubmit}
              required
              disabled={isDisabled}
            />
          </Box>
        </Box>
      </Box>
    </FormCardSection>
  );
});

export type ClientCorporateInformationProps = {
  formValues: ClientDataForm;
  updateFormValue: (key: string, value: string | number) => void;
  formErrors: Record<string, string>;
  id?: string;
  onSubmit?: (event: React.KeyboardEvent<HTMLElement>) => void;
};
